import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { UtilsService } from '../utils/utils.service';

/**
 * Per-agent calls that don't fit VapiService's generic ai_agents/vapi orchestration endpoints —
 * each AI agent with its own dedicated backend model (e.g. models/Ivy.php) gets its calls here.
 */
@Injectable({
	providedIn: 'root',
})
export class AiAgentsService {
	constructor(private http: HttpClient, private utils: UtilsService) {}

	/**
	 * Rebuilds the agent's prospect queue (ai_agents_queue) from scratch, in the given campaign
	 * order, and saves that same order to ai_campaigns_queue — mirrors the campaign queue the user
	 * just set in the Configure modal. Ivy.php reads agent_id/campaign_ids straight off $_POST as
	 * an array (no JSON-decode step), so this sends the campaign_ids[] bracket form-field
	 * convention rather than a JSON-encoded string.
	 */
	setIvyProspects(agentId: number | undefined, campaignIds: number[]) {
		const url = `${environment.api}ivy/set_prospects`;
		const formData = new FormData();
		if (agentId !== undefined) formData.append('agent_id', String(agentId));
		for (const id of campaignIds) formData.append('campaign_ids[]', String(id));
		return this.http.post(url, formData);
	}

	/**
	 * Creates a running INSTANCE of a catalog agent (ai_agents row, from ai_agents_descriptions) and
	 * starts it dialing: builds its prospect queue from the given campaigns (in that order) and
	 * places the first call from the chosen Vapi phone number. Several instances of the same agent
	 * can run at once, each on its own number and its own campaigns — the backend rejects (400,
	 * with a readable `message`) a number or a campaign that another running instance already has.
	 *
	 * Renee sends `mode` too ('campaigns' = the one campaign in campaign_ids, 'all_campaigns' =
	 * campaign_ids empty), no date: her queue (ai_queue_renee) is every lead pending a reschedule.
	 *
	 * Parker also sends `mode` ('campaigns' = the one campaign in campaign_ids, 'all_campaigns' =
	 * campaign_ids empty) and `start_date` (YYYY-MM-DD): its queue (ai_parker_queue) is every lead
	 * (status 2) whose meeting (leads.date_generated) falls between that date and today.
	 */
	startInstance(fields: {
		description_id: number;
		phone_number_id: string;
		campaign_ids: number[];
		user_id?: number;
		mode?: 'campaigns' | 'all_campaigns';
		start_date?: string;
	}) {
		const url = `${environment.api}agents/start_instance`;
		const formData = this.utils.jsonToFormData({
			description_id: fields.description_id,
			phone_number_id: fields.phone_number_id,
			campaign_ids: JSON.stringify(fields.campaign_ids ?? []),
			...(fields.user_id !== undefined ? { user_id: fields.user_id } : {}),
			...(fields.mode ? { mode: fields.mode } : {}),
			...(fields.start_date ? { start_date: fields.start_date } : {}),
		});
		return this.http.post(url, formData);
	}

	/**
	 * Running agent instances (ai_agents rows with status 'active' or 'paused') for the Live floor:
	 * phone number id, ordered campaign names, queue progress (queue_called / queue_total) and the
	 * latest action (last_action CALLING = who it's on the phone with right now).
	 */
	getInstances() {
		const url = `${environment.api}agents/get_instances`;
		return this.http.get(url);
	}

	/**
	 * Pause / resume / stop one agent instance. None of them cuts the call in progress — they only
	 * decide what happens once it ends. Stop is final: the instance is finished and its phone
	 * number is freed. Responds with the instance's resulting status (+ a message when a resume
	 * couldn't place the next call).
	 */
	setInstanceAction(agentId: number, action: 'pause' | 'resume' | 'stop') {
		const url = `${environment.api}agents/set_instance_action`;
		const formData = this.utils.jsonToFormData({ agent_id: agentId, action });
		return this.http.post(url, formData);
	}

	/** The campaign queue currently saved for an agent (ai_campaigns_queue), ordered — used to prefill the Configure modal on open. */
	getCampaignsQueue(agentId: number) {
		const url = `${environment.api}ivy/${agentId}/get_campaigns_queue`;
		return this.http.get(url);
	}

	/** Prospects queued for one agent instance (ai_agents_queue joined to prospects) — powers the Prospects modal. */
	getProspects(agentId: number) {
		const url = `${environment.api}agents/get_prospects`;
		const formData = this.utils.jsonToFormData({ agent_id: agentId });
		return this.http.post(url, formData);
	}

	/** ai_calls log of one agent instance, most recent first — powers the Calls modal opened from the Live floor. */
	getCalls(agentId: number) {
		const url = `${environment.api}agents/${agentId}/get_calls`;
		return this.http.get(url);
	}

	/** ai_calls log of every instance ever created from a catalog agent (finished ones included) — powers the Calls modal opened from the agent's card. */
	getCallsByDescription(descriptionId: number) {
		const url = `${environment.api}agents/${descriptionId}/get_calls_by_description`;
		return this.http.get(url);
	}
}
